import { useReducer, useCallback } from "react";
import { featureGatingApi } from "./useFeatureGatingApi";
import featureGatingReducer, { FEATURE_GATING_ACTIONS } from "./useFeatureGatingReducer";
import { FEATURE_ERROR_MESSAGES } from "../constants";

export const useFeatureGatingManagement = () => {
  const [state, dispatch] = useReducer(featureGatingReducer, {
    features: [],
    loading: false,
    error: null,
    selectedFeature: null,
  });

  // Clear error
  const clearError = useCallback(() => {
    dispatch({ type: FEATURE_GATING_ACTIONS.CLEAR_ERROR });
  }, []);

  // Fetch all features
  const fetchFeatures = useCallback(async () => {
    dispatch({ type: FEATURE_GATING_ACTIONS.SET_LOADING, payload: true });
    try {
      const features = await featureGatingApi.getAllFeatures();
      dispatch({ type: FEATURE_GATING_ACTIONS.SET_FEATURES, payload: features });
    } catch (error) {
      dispatch({
        type: FEATURE_GATING_ACTIONS.SET_ERROR,
        payload: error.response?.data?.["error-message"] || FEATURE_ERROR_MESSAGES.FETCH_FEATURES,
      });
    }
  }, []);

  // Get feature by ID
  const getFeatureById = useCallback(async featureId => {
    dispatch({ type: FEATURE_GATING_ACTIONS.SET_LOADING, payload: true });
    try {
      const feature = await featureGatingApi.getFeatureById(featureId);
      dispatch({ type: FEATURE_GATING_ACTIONS.SET_SELECTED_FEATURE, payload: feature });
      return feature;
    } catch (error) {
      const errorMessage = error.response?.data?.["error-message"] || FEATURE_ERROR_MESSAGES.FETCH_FEATURE;
      dispatch({ type: FEATURE_GATING_ACTIONS.SET_ERROR, payload: errorMessage });
      throw error;
    }
  }, []);

  // Create or update feature
  const createOrUpdateFeature = useCallback(async (featureName, featureData) => {
    dispatch({ type: FEATURE_GATING_ACTIONS.SET_LOADING, payload: true });
    try {
      const feature = await featureGatingApi.createOrUpdateFeature(featureName, featureData);
      dispatch({ type: FEATURE_GATING_ACTIONS.UPDATE_FEATURE, payload: feature });
      return feature;
    } catch (error) {
      const errorMessage = error.response?.data?.["error-message"] || FEATURE_ERROR_MESSAGES.CREATE_UPDATE_FEATURE;
      dispatch({ type: FEATURE_GATING_ACTIONS.SET_ERROR, payload: errorMessage });
      throw error;
    }
  }, []);

  // Update gating status
  const updateGatingStatus = useCallback(async (featureId, gated) => {
    dispatch({ type: FEATURE_GATING_ACTIONS.SET_LOADING, payload: true });
    try {
      const updatedFeature = await featureGatingApi.updateGatingStatus(featureId, gated);
      dispatch({ type: FEATURE_GATING_ACTIONS.UPDATE_FEATURE, payload: updatedFeature });
      return updatedFeature;
    } catch (error) {
      const errorMessage = error.response?.data?.["error-message"] || FEATURE_ERROR_MESSAGES.UPDATE_GATING_STATUS;
      dispatch({ type: FEATURE_GATING_ACTIONS.SET_ERROR, payload: errorMessage });
      throw error;
    }
  }, []);

  // Update feature users
  const updateFeatureUsers = useCallback(async (featureId, usersData) => {
    dispatch({ type: FEATURE_GATING_ACTIONS.SET_LOADING, payload: true });
    try {
      const updatedFeature = await featureGatingApi.updateFeatureUsers(featureId, usersData);
      dispatch({ type: FEATURE_GATING_ACTIONS.UPDATE_FEATURE, payload: updatedFeature });
      return updatedFeature;
    } catch (error) {
      const errorMessage = error.response?.data?.["error-message"] || FEATURE_ERROR_MESSAGES.UPDATE_FEATURE_USERS;
      dispatch({ type: FEATURE_GATING_ACTIONS.SET_ERROR, payload: errorMessage });
      throw error;
    }
  }, []);

  // Delete feature
  const deleteFeature = useCallback(async featureId => {
    dispatch({ type: FEATURE_GATING_ACTIONS.SET_LOADING, payload: true });
    try {
      await featureGatingApi.deleteFeature(featureId);
      dispatch({ type: FEATURE_GATING_ACTIONS.DELETE_FEATURE, payload: featureId });
    } catch (error) {
      const errorMessage = error.response?.data?.["error-message"] || FEATURE_ERROR_MESSAGES.DELETE_FEATURE;
      dispatch({ type: FEATURE_GATING_ACTIONS.SET_ERROR, payload: errorMessage });
      throw error;
    }
  }, []);

  // Select feature
  const selectFeature = useCallback(feature => {
    dispatch({ type: FEATURE_GATING_ACTIONS.SET_SELECTED_FEATURE, payload: feature });
  }, []);

  // Reset state
  const resetState = useCallback(() => {
    dispatch({ type: FEATURE_GATING_ACTIONS.RESET_STATE });
  }, []);

  return {
    // State
    features: state.features,
    loading: state.loading,
    error: state.error,
    selectedFeature: state.selectedFeature,

    // Actions
    fetchFeatures,
    getFeatureById,
    createOrUpdateFeature,
    updateGatingStatus,
    updateFeatureUsers,
    deleteFeature,
    selectFeature,
    clearError,
    resetState,
  };
};
